import { Link, NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { ChangePasswordButton, LogoutButton } from "@/components/ChangePasswordButton";
import { BrandMark } from "@/components/BrandMark";
import { Shield, Image as ImageIcon, LayoutDashboard, Package, Users, FileCheck2, Mail, Percent, Key, Store, FileText, UploadCloud, UserPlus, Receipt, BookOpenCheck, Lock, AlertTriangle } from "lucide-react";
import { useMe } from "@/lib/auth";

const adminNav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/resellers", label: "Rivenditori", icon: Users },
  { to: "/admin/customers", label: "Clienti", icon: Store },
  { to: "/admin/products", label: "Prodotti", icon: Package },
  { to: "/admin/approvals", label: "Approvazioni", icon: FileCheck2 },
  { to: "/admin/commissions", label: "Provvigioni", icon: Percent },
  { to: "/admin/api-keys", label: "Chiavi API", icon: Key },
  { to: "/admin/smtp", label: "SMTP", icon: Mail },
  { to: "/admin/branding", label: "Logo", icon: ImageIcon },
  { to: "/developer", label: "API Docs", icon: BookOpenCheck },
];

const resellerNav = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/profile", label: "Anagrafica", icon: FileText },
  { to: "/portal/documents", label: "Documenti", icon: UploadCloud },
  { to: "/portal/products", label: "Prodotti", icon: Store },
  { to: "/portal/customers", label: "Clienti", icon: Users, end: true },
  { to: "/portal/customers/new", label: "Nuovo cliente", icon: UserPlus },
  { to: "/portal/commissions", label: "Provvigioni", icon: Receipt },
  { to: "/portal/plan", label: "Piano provvigioni", icon: Percent },
];

export default function AppLayout() {
  const { data: me, isLoading } = useMe();
  const navigate = useNavigate();
  const loc = useLocation();

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="layout-loading">Caricamento…</div>;
  }

  const role = me?.user?.role;
  const nav = role === "admin" ? adminNav : resellerNav;
  const brandColor = role === "admin" ? "bg-[#1E2A38] text-white" : "bg-[#B84A2B] text-white";

  // Redirect root roles
  if (role === "admin" && (loc.pathname === "/" || loc.pathname === "/portal")) {
    navigate("/admin", { replace: true });
  }
  if (role === "reseller" && (loc.pathname === "/" || loc.pathname === "/admin")) {
    navigate("/portal", { replace: true });
  }

  const banner = (() => {
    if (me?.blocked) {
      return (
        <div className="bg-rose-50 border-l-4 border-rose-600 p-4 text-rose-900 rounded-r-lg flex items-center gap-3" data-testid="banner-blocked">
          <Lock size={18} />
          <div>
            <div className="font-semibold">Accesso bloccato dal Super Admin</div>
            <div className="text-sm">L'account rivenditore è stato sospeso. I Suoi clienti continuano a lavorare normalmente. Contatti GoWeb Srl.</div>
          </div>
        </div>
      );
    }
    if (me?.read_only) {
      return (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 text-amber-900 rounded-r-lg flex items-center gap-3" data-testid="banner-readonly">
          <AlertTriangle size={18} />
          <div>
            <div className="font-semibold">Accesso in sola lettura</div>
            <div className="text-sm">Alcune funzioni (nuovo cliente, modifiche) sono disabilitate finché il Super Admin non approva la Sua modulistica.</div>
          </div>
        </div>
      );
    }
    return null;
  })();

  return (
    <div className="min-h-screen flex bg-[#F9F8F6] text-slate-900">
      <aside className="w-64 flex-shrink-0 hidden md:flex flex-col border-r border-[#E2DDD5] bg-white">
        <Link to="/" className="h-16 px-5 flex items-center gap-2 border-b border-[#E2DDD5]">
          <BrandMark className={brandColor} />
          <div>
            <div className="text-sm font-bold tracking-tight">GoWeb Reseller</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              {role === "admin" ? "Super Admin" : "Rivenditore"}
            </div>
          </div>
        </Link>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.to.split("/").join("-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-[#B84A2B] text-white shadow-sm"
                    : "text-slate-700 hover:bg-[#F2F0EB]"
                }`
              }
            >
              <n.icon size={16} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#E2DDD5] text-[11px] text-slate-500">
          <div className="font-mono">reseller.goweb.srl</div>
          <div className="mt-1">GoWeb Srl Semplificata</div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-h-screen min-w-0">
        <header className="h-16 px-6 flex items-center justify-between sticky top-0 z-30 backdrop-blur-md bg-white/80 border-b border-[#E2DDD5]">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">
              {role === "admin" ? "Pannello Super Admin" : "Area Rivenditore"}
            </div>
            <div className="text-base font-semibold text-slate-900">
              {me?.reseller?.company?.ragione_sociale || me?.user?.email}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {me?.reseller?.codice_reseller && (
              <span className="font-mono text-xs bg-[#F2F0EB] text-slate-700 px-2 py-1 rounded border border-[#E2DDD5]" data-testid="reseller-code">
                {me.reseller.codice_reseller}
              </span>
            )}
            <ChangePasswordButton forced={!!me?.user?.must_change_password} />
            <LogoutButton />
          </div>
        </header>
        <div className="p-6 flex-1 space-y-4">
          {banner}
          <Outlet context={me} />
        </div>
      </main>
    </div>
  );
}
